import { legalAcceptanceRequired, presentLegalAcceptance } from "./legal_acceptance.js"
import { OFFLINE_CACHE_PREFIX, OfflinePdfStore } from "./offline_pdf_store.js"
import { pauseOfflinePdfs, refreshOfflinePdfs } from "./offline_pdf_access.js"
import { withOfflineCleanupLock } from "./offline_download_lock.js"

const REFRESH_RETRY_INTERVAL = 5000
let lastUserId
let refreshPromise
let refreshRetryTimer

export async function initializeOfflineApp({ storeFactory = () => new OfflinePdfStore(), lockManager = globalThis.navigator?.locks } = {}) {
  const userId = document.querySelector("meta[name='offline-user-id']")?.content
  if (userId) {
    if (lastUserId !== userId && globalThis.indexedDB) {
      const store = storeFactory()
      let switchComplete = false
      try {
        const previousUserId = await store.activeUser()
        const pendingUserCleanupId = await store.pendingUserCleanup()
        const cleanupUserId = pendingUserCleanupId ?? previousUserId
        const cleanupRequired = pendingUserCleanupId !== null || (cleanupUserId !== null && String(cleanupUserId) !== userId)

        if (cleanupRequired) {
          let cleanupFailed = false
          const coordinated = await withOfflineCleanupLock(async () => {
            try {
              // This marker must commit before access is disabled because
              // setActiveUser(null) removes the only other durable identity.
              await store.setPendingUserCleanup(cleanupUserId)
            } catch (error) {
              cleanupFailed = true
              console.error("Unable to remember previous user's pending cleanup", error)
              return
            }

            // Remove eligibility before fallible file cleanup so the previous
            // account cannot remain readable during a partially completed switch.
            try {
              await disableOfflineAccess(store, { userId: cleanupUserId })
            } catch (error) {
              cleanupFailed = true
              console.error("Unable to disable previous user's offline access", error)
            }

            try {
              await store.clearUser(cleanupUserId)
            } catch (error) {
              cleanupFailed = true
              console.error("Unable to clean up previous user's offline content", error)
            }

            if (!cleanupFailed) {
              try {
                await store.clearPendingUserCleanup()
              } catch (error) {
                cleanupFailed = true
                console.error("Unable to finish previous user's offline cleanup", error)
              }
            }
          }, { lockManager })

          if (!coordinated || cleanupFailed) return
        }

        await store.setActiveUser(Number(userId))
        switchComplete = true
      } catch (error) {
        console.error("Unable to initialize offline access for the active user", error)
      } finally {
        await store.closeDatabase().catch((error) => {
          console.error("Unable to close offline storage after account switch", error)
        })
      }
      if (!switchComplete) return
    }
    lastUserId = userId
    await registerServiceWorker()
    await cacheOfflineLibrary(userId)
    await refreshStoredPdfs(userId)
    return
  }

  if (lastUserId !== null && globalThis.indexedDB) {
    const store = storeFactory()
    let cleanupComplete = false
    let disableError
    try {
      const coordinated = await withOfflineCleanupLock(async () => {
        // Disable the previous namespace before deleting files so a partial cleanup
        // can never leave a private cached reader available while signed out.
        try {
          await disableOfflineAccess(store)
        } catch (error) {
          disableError = error
        }

        try {
          await store.clearAll()
          cleanupComplete = true
        } catch (error) {
          if (disableError) console.error("Unable to disable signed-out offline access", disableError)
          console.error("Unable to complete signed-out offline cleanup", error)
        }
      }, { lockManager })
      if (!coordinated) console.error("Unable to coordinate signed-out offline cleanup")
    } catch (error) {
      console.error("Unable to coordinate signed-out offline cleanup", error)
    } finally {
      await store.closeDatabase().catch((error) => {
        console.error("Unable to close offline storage after cleanup", error)
      })
    }
    if (!cleanupComplete) return
  }
  lastUserId = null
}

async function disableOfflineAccess(store, { userId } = {}) {
  const failures = []

  try {
    await store.setActiveUser(null)
  } catch (error) {
    failures.push(error)
  }

  if (globalThis.caches) {
    try {
      const names = await caches.keys()
      const cacheNames = userId === undefined
        ? names.filter((name) => name.startsWith(OFFLINE_CACHE_PREFIX))
        : names.filter((name) => name === `${OFFLINE_CACHE_PREFIX}${userId}`)
      await Promise.all(cacheNames.map((name) => caches.delete(name)))
    } catch (error) {
      failures.push(error)
    }
  }

  if (failures.length > 1) throw new AggregateError(failures, "Unable to disable offline access")
  if (failures.length === 1) throw failures[0]
}

async function refreshStoredPdfs(userId) {
  window.clearTimeout(refreshRetryTimer)
  if (document.querySelector("[data-controller~='pdf-reader']")) return
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const store = new OfflinePdfStore()
    try {
      if (!store.supported()) return

      const result = await refreshOfflinePdfs(Number(userId), { store })
      if (result.status === "legal_acceptance_required") presentLegalAcceptance(result)
      if (result.retry && navigator.onLine) {
        refreshRetryTimer = window.setTimeout(() => refreshStoredPdfs(userId), REFRESH_RETRY_INTERVAL)
      }
    } finally {
      await store.closeDatabase()
    }
  })()

  try {
    await refreshPromise
  } finally {
    refreshPromise = null
  }
}

async function cacheOfflineLibrary(userId) {
  if (!globalThis.caches) return

  try {
    const response = await fetch("/offline", { credentials: "same-origin", headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
    const legal = await legalAcceptanceRequired(response)
    if (legal) { presentLegalAcceptance(legal); return }
    if (!response.ok || response.redirected) return
    const cache = await caches.open(`${OFFLINE_CACHE_PREFIX}${userId}`)
    await cache.put("/offline", response)
  } catch (_error) {
    // An existing user-scoped shell remains available during a network interruption.
  }
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return

  try {
    await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
  } catch (error) {
    console.error("Unable to register offline support", error)
  }
}

window.addEventListener("online", () => {
  const userId = document.querySelector("meta[name='offline-user-id']")?.content
  if (userId) refreshStoredPdfs(userId)
})

// Consent can be discovered by any background client, including on exempt pages.
window.addEventListener("legal-acceptance:required", async (event) => {
  const userId = document.querySelector("meta[name='offline-user-id']")?.content
  if (!userId || document.querySelector("[data-controller~='pdf-reader']")) return
  const store = new OfflinePdfStore()
  try {
    if (store.supported()) await pauseOfflinePdfs(store, { userId: Number(userId) }, event.detail.acceptanceUrl)
  } catch (error) {
    console.warn("Unable to persist account consent pause", error)
  } finally {
    await store.closeDatabase()
  }
})
